/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @LastEditors: Ryan
 * @LastEditTime: 2026-01-26
 */
import dynamic from 'next/dynamic';
import React from 'react';

import ImageGeneratorSkeleton from '@/3.0/image-generator/_components/image-generator-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 预加载资源
const PRELOAD_POSTER =
  'https://assets.falcocut.ai/ai-image-generator/hero-image-poster.webp';

// 动态加载完整的 ImageGeneration
const ImageGeneration = dynamic(() => import('../../3.0/image-generator/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <ImageGeneratorSkeleton />
    </DelayedRender>
  ),
});

const SEOSections = dynamic(() => import('../../3.0/image-generator/seo'), {
  ssr: true,
});

// SEO 常量
const PAGE_TITLE = 'AI Image Generator - Create Images with AI | FalcoCut';
const META_DESCRIPTION =
  'Create stunning AI-powered images in seconds. Transform text to images, generate unique artwork, and produce professional visuals with FalcoCut AI Image Generator.';
const CURRENT_URL = 'https://falcocut.ai/ai-image-generator';

// 结构化数据
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Image Generator',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.9',
  ratingCount: '3850',
  featureList: [
    'Text to image generation',
    'AI artwork creation',
    'Multiple AI models',
    'High-resolution output',
    'Style customization',
    'Professional image templates',
  ],
});

const ImageGeneratorPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Image Generator"
        publishedTime="2026-01-26"
        modifiedTime="2026-01-26"
        structuredData={structuredData}
        preloadImages={[PRELOAD_POSTER]}
      />

      {/* 客户端动态加载完整组件（保留骨架屏） */}
      <ImageGeneration />
    </>
  );
};

// 定义 getLayout 为直接返回页面本身，不使用 Layout
ImageGeneratorPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// 定义 SEO 部分和 Footer 作为额外内容，在 Layout 外渲染
ImageGeneratorPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default ImageGeneratorPage;
